import React, { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { ListingsContext } from '@fiverr-private/listings';
import PersonalizedSection from '../PersonalizedSection/PersonalizedSection.lazy';
import { PersonalizedComponentMapper } from '../PersonalizedComponentMapper/ComponentMapper.lazy';
import { logger, extractRecommendationsParams } from '../../shared/logger';
import { usePersonalizedData } from '../../hooks/usePersonalizedData';
import { useCollections } from '../../hooks/useCollections';
import { PersonalizedSectionFetcherProps } from './types';

const PersonalizedSectionFetcher = ({
    appName,
    perseusName,
    onFetchSucceeded,
    onFetchFailed,
    recommendationsParams = {},
    collectProps = {},
    shouldFetchCollections = false,
    fallback,
    placeholder,
    complianceData,
    withPolling = false,
    ...rest
}: PersonalizedSectionFetcherProps) => {
    logger.setLoggerConsumerData(extractRecommendationsParams({ appName, recommendationsParams }));

    const { fetchPersonalizedData, fetchStatus, personalizedData } = usePersonalizedData({
        appName,
        perseusName,
        onFetchFailed,
        onFetchSucceeded,
        recommendationsParams,
        withPolling,
        ...rest,
    });

    const { collectProps: overriddenCollectProps } = useCollections({
        shouldFetchCollections,
        collectProps,
        appName,
        logAttributes: {
            perseusName,
            recommendationsParams,
            sectionName: rest?.sectionName,
        },
    });

    useEffect(() => {
        fetchPersonalizedData();
    }, [fetchPersonalizedData]);

    // Used to override personalizedData with props.personalizedContent if passed
    const stateWithProps = {
        ...fetchStatus,
        personalizedContent: personalizedData,
        ...rest,
        collectProps: overriddenCollectProps,
    };
    const { hasError, isLoading } = fetchStatus;
    const shouldRender = !hasError && !isLoading && !isEmpty(stateWithProps.personalizedContent);
    const shouldRenderPlaceholder = !!placeholder && isLoading;

    if (shouldRenderPlaceholder) {
        return placeholder;
    }

    if (!shouldRender) {
        return null;
    }

    return (
        <ListingsContext complianceData={complianceData}>
            <PersonalizedSection
                onError={onFetchFailed}
                PersonalizedComponentMapper={PersonalizedComponentMapper}
                fallback={fallback}
                placeholder={placeholder}
                appName={appName}
                {...stateWithProps}
                personalizedContent={stateWithProps.personalizedContent}
            />
        </ListingsContext>
    );
};

export default PersonalizedSectionFetcher;
